// Packages: the index by type; the open package in the right panel. Publishing stays
// greyed until the server offers a route (docs/server-requests/dashboard-package-publish.md).
import React from 'react';
import type { PackageRow } from '../../../shared/types';
import { GAsyncState, GCard, GEyebrow, GH2, GPill, GStatus, GTabs, inputStyle } from '../design/atoms';
import { DbPending, isDbUnavailable } from '../design/DbPending';
import { DetailHeader, DetailSection, MasterDetail, ROW_CAP, RowCapNote, selectedRow, useEscape } from '../design/MasterDetail';
import { fmtAge, fmtBytes, fmtDateTime } from '../fmt';
import { useResource } from '../store/resourceStore';
import { useSelection } from '../store/uiStore';

type Tab = 'dcc' | 'folder_structure' | 'template' | 'other' | 'all';
const TABS: ReadonlyArray<{ id: Tab; label: string }> = [
  { id: 'all', label: 'All' }, { id: 'dcc', label: 'DCCs' }, { id: 'folder_structure', label: 'Folder structures' }, { id: 'template', label: 'Templates' }, { id: 'other', label: 'Other' },
];
const PUBLISH_WHY = 'Publishing needs a route the dashboard can call. Asked of the server team on 2026-10-21 (docs/server-requests/dashboard-package-publish.md).';

function tabOf(p: PackageRow): Tab {
  return p.category === 'dcc' || p.category === 'folder_structure' || p.category === 'template' ? p.category : 'other';
}

function Detail({ p, versions, onClose }: { p: PackageRow; versions: PackageRow[]; onClose: () => void }) {
  const fact = (k: string, v: React.ReactNode) => <div style={{ overflowWrap: 'anywhere' }}><span style={{ color: 'var(--fg-4)' }}>{k} </span>{v}</div>;
  return (
    <>
      <DetailHeader title={p.displayName} eyebrow={`${p.packageName} · ${p.version} · ${p.scope === 'official' ? 'Official' : p.studioLabel ?? 'studio'}`} onClose={onClose}
        right={<GPill disabled title={PUBLISH_WHY}>New version</GPill>} />
      {p.description && <div style={{ fontFamily: 'var(--ff-sans)', fontSize: 13, color: 'var(--fg-2)', whiteSpace: 'pre-wrap', marginBottom: 6 }}>{p.description.slice(0, 1200)}</div>}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontFamily: 'var(--ff-code)', fontSize: 12.5 }}>
        {fact('category', p.category ?? 'none')}
        {fact('tags', p.tags.join(', ') || '–')}
        {fact('author', p.author)}
        {fact('size', fmtBytes(p.archiveSize))}
        {fact('dependencies', p.dependencies)}
        {fact('published', fmtDateTime(p.publishedAt))}
        {fact('unpublished', p.unpublishedAt ? fmtDateTime(p.unpublishedAt) : '–')}
        {fact('archive sha256', p.archiveHash)}
        {fact('id', p.id)}
        {p.studioId && fact('studio', p.studioId)}
      </div>
      <DetailSection label={`Versions of ${p.packageName} · ${versions.length}`}>
        <table className="pd" style={{ fontSize: 12 }}>
          <thead><tr><th>Version</th><th>Published</th><th>State</th></tr></thead>
          <tbody>{versions.map((v) => <tr key={v.id} style={{ background: v.id === p.id ? 'var(--bg-elev)' : undefined }}><td style={{ fontFamily: 'var(--ff-code)' }}>{v.version}</td><td style={{ whiteSpace: 'nowrap' }}>{fmtAge(v.publishedAt)}</td><td>{v.unpublishedAt ? <GStatus kind="dim" label="unpublished" /> : <GStatus kind="ok" label="published" />}</td></tr>)}</tbody>
        </table>
      </DetailSection>
    </>
  );
}

export function Packages() {
  const r = useResource<PackageRow[]>('/api/packages');
  const [tab, setTab] = React.useState<Tab>('all');
  const [q, setQ] = React.useState('');
  const [showUnpublished, setShowUnpublished] = React.useState(false);
  const [open, setOpen] = useSelection('packages');
  const close = React.useCallback(() => setOpen(null), [setOpen]);
  useEscape(close, !!open);
  const all = r.data ?? [];
  const counts = { all: 0, dcc: 0, folder_structure: 0, template: 0, other: 0 } as Record<Tab, number>;
  for (const p of all) if (!p.unpublishedAt) { counts.all++; counts[tabOf(p)]++; }
  const filtered = all.filter((p) => (tab === 'all' || tabOf(p) === tab) && (showUnpublished || !p.unpublishedAt) && (!q || [p.displayName, p.packageName, p.author, p.studioLabel, ...p.tags].some((v) => v?.toLowerCase().includes(q.toLowerCase()))));
  const rows = filtered.slice(0, ROW_CAP);
  const total = all.reduce((s, p) => s + (p.unpublishedAt ? 0 : p.archiveSize), 0);
  const official = all.filter((p) => p.scope === 'official' && !p.unpublishedAt).length;
  const current = all.find((p) => p.id === open) ?? null;
  const versions = current ? all.filter((p) => p.packageName === current.packageName && p.scope === current.scope && p.studioId === current.studioId).sort((a, b) => (a.publishedAt < b.publishedAt ? 1 : -1)) : [];

  const list = isDbUnavailable(r.error) ? <DbPending error={r.error!} onRetry={r.reload} /> : (
    <GCard padding="18px 20px">
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
        <GH2 size={17}>Packages</GH2>
        <GEyebrow style={{ fontSize: 12 }}>{r.data ? `${counts.all} published · ${fmtBytes(total)} · ${official} in the Official catalogue` : ''}</GEyebrow>
        <div style={{ flex: 1 }} />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="search name, author, studio or tag" style={{ ...inputStyle, width: 260, padding: '7px 12px' }} />
        <GPill onClick={() => setShowUnpublished((v) => !v)} solid={showUnpublished}>{showUnpublished ? 'Hiding nothing' : 'Show unpublished'}</GPill>
        <GPill onClick={r.reload} disabled={r.loading}>Refresh</GPill>
        <GPill signal disabled title={PUBLISH_WHY}>Publish package</GPill>
      </div>
      <GTabs items={TABS.map((t) => ({ id: t.id, label: `${t.label} ${counts[t.id]}` }))} value={tab} onChange={setTab} ariaLabel="Package type" style={{ marginBottom: 12 }} />
      <GAsyncState busy={r.loading && !r.data} error={r.error} onRetry={r.reload} />
      {r.data && (rows.length === 0 ? <div style={{ fontFamily: 'var(--ff-code)', fontSize: 12.5, color: 'var(--fg-4)' }}>Nothing in this tab.</div> : (
        <>
          <table className="pd">
            <thead><tr><th>Package</th><th>Version</th><th>Scope</th><th>Tags</th><th>Author</th><th className="num">Size</th><th>Published</th><th>State</th></tr></thead>
            <tbody>{rows.map((p) => (
              <tr key={p.id} className="row-hover" aria-selected={open === p.id || undefined} style={selectedRow(open === p.id)} onClick={() => setOpen(p.id)}>
                <td>{p.displayName} <span style={{ color: 'var(--fg-4)', fontFamily: 'var(--ff-code)', fontSize: 12 }}>{p.packageName}</span></td>
                <td style={{ fontFamily: 'var(--ff-code)', fontSize: 12 }}>{p.version}</td>
                <td style={{ fontFamily: 'var(--ff-code)', fontSize: 12 }}>{p.scope === 'official' ? 'Official' : p.studioLabel ?? (p.studioId ? p.studioId.slice(0, 8) + '…' : 'studio')}</td>
                <td style={{ fontFamily: 'var(--ff-code)', fontSize: 11.5, color: 'var(--fg-3)' }}>{p.tags.join(', ')}</td>
                <td>{p.author}</td>
                <td className="num">{fmtBytes(p.archiveSize)}</td>
                <td style={{ whiteSpace: 'nowrap' }} title={p.publishedAt}>{fmtAge(p.publishedAt)}</td>
                <td>{p.unpublishedAt ? <GStatus kind="dim" label="unpublished" /> : <GStatus kind="ok" label="published" />}</td>
              </tr>
            ))}</tbody>
          </table>
          <RowCapNote shown={rows.length} total={filtered.length} />
        </>
      ))}
    </GCard>
  );
  return <MasterDetail list={list} detail={current ? <Detail key={current.id} p={current} versions={versions} onClose={close} /> : null} />;
}
